import { alpha, createTheme } from "@mui/material/styles";
import typography from "./typography";
import { DEFAULT_WALLET_THEME } from "./defaultWalletTheme";
import { ResolvedWalletTheme } from "./walletTheme.types";
import { extendPaletteColor } from "./paletteUtils";
import { normalizeWalletTheme } from "./normalizeWalletTheme";

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

const createShadow = (color: string) => {
  const transparent1 = alpha(color, 0.2);
  const transparent2 = alpha(color, 0.14);
  const transparent3 = alpha(color, 0.12);
  return [
    "none",
    `0px 2px 1px -1px ${transparent1},0px 1px 1px 0px ${transparent2},0px 1px 3px 0px ${transparent3}`,
    `0px 3px 1px -2px ${transparent1},0px 2px 2px 0px ${transparent2},0px 1px 5px 0px ${transparent3}`,
    `0px 3px 3px -2px ${transparent1},0px 3px 4px 0px ${transparent2},0px 1px 8px 0px ${transparent3}`,
    `0px 2px 4px -1px ${transparent1},0px 4px 5px 0px ${transparent2},0px 1px 10px 0px ${transparent3}`,
    `0px 3px 5px -1px ${transparent1},0px 5px 8px 0px ${transparent2},0px 1px 14px 0px ${transparent3}`,
    `0px 3px 5px -1px ${transparent1},0px 6px 10px 0px ${transparent2},0px 1px 18px 0px ${transparent3}`,
    `0px 4px 5px -2px ${transparent1},0px 7px 10px 1px ${transparent2},0px 2px 16px 1px ${transparent3}`,
    `0px 5px 5px -3px ${transparent1},0px 8px 10px 1px ${transparent2},0px 3px 14px 2px ${transparent3}`,
    `0px 5px 6px -3px ${transparent1},0px 9px 12px 1px ${transparent2},0px 3px 16px 2px ${transparent3}`,
    `0px 6px 6px -3px ${transparent1},0px 10px 14px 1px ${transparent2},0px 4px 18px 3px ${transparent3}`,
    `0px 6px 7px -4px ${transparent1},0px 11px 15px 1px ${transparent2},0px 4px 20px 3px ${transparent3}`,
    `0px 7px 8px -4px ${transparent1},0px 12px 17px 2px ${transparent2},0px 5px 22px 4px ${transparent3}`,
    `0px 7px 8px -4px ${transparent1},0px 13px 19px 2px ${transparent2},0px 5px 24px 4px ${transparent3}`,
    `0px 7px 9px -4px ${transparent1},0px 14px 21px 2px ${transparent2},0px 5px 26px 4px ${transparent3}`,
    `0px 8px 9px -5px ${transparent1},0px 15px 22px 2px ${transparent2},0px 6px 28px 5px ${transparent3}`,
    `0px 8px 10px -5px ${transparent1},0px 16px 24px 2px ${transparent2},0px 6px 30px 5px ${transparent3}`,
    `0px 8px 11px -5px ${transparent1},0px 17px 26px 2px ${transparent2},0px 6px 32px 5px ${transparent3}`,
    `0px 9px 11px -5px ${transparent1},0px 18px 28px 2px ${transparent2},0px 7px 34px 6px ${transparent3}`,
    `0px 9px 12px -6px ${transparent1},0px 19px 29px 2px ${transparent2},0px 7px 36px 6px ${transparent3}`,
    `0px 10px 13px -6px ${transparent1},0px 20px 31px 3px ${transparent2},0px 8px 38px 7px ${transparent3}`,
    `0px 10px 13px -6px ${transparent1},0px 21px 33px 3px ${transparent2},0px 8px 40px 7px ${transparent3}`,
    `0px 10px 14px -6px ${transparent1},0px 22px 35px 3px ${transparent2},0px 8px 42px 7px ${transparent3}`,
    `0px 11px 14px -7px ${transparent1},0px 23px 36px 3px ${transparent2},0px 9px 44px 8px ${transparent3}`,
    `0px 11px 15px -7px ${transparent1},0px 24px 38px 3px ${transparent2},0px 9px 46px 8px ${transparent3}`,
  ] as ReturnType<typeof createTheme>["shadows"];
};

const createCustomShadow = (grey500: string, palette: ResolvedWalletTheme) => {
  const transparent = alpha(grey500, 0.24);

  return {
    z1: `0 1px 2px 0 ${transparent}`,
    z8: `0 8px 16px 0 ${transparent}`,
    z12: `0 0 2px 0 ${transparent}, 0 12px 24px 0 ${transparent}`,
    z16: `0 0 2px 0 ${transparent}, 0 16px 32px -4px ${transparent}`,
    z20: `0 0 2px 0 ${transparent}, 0 20px 40px -4px ${transparent}`,
    z24: `0 0 4px 0 ${transparent}, 0 24px 48px 0 ${transparent}`,
    primary: `0 8px 16px 0 ${alpha(palette.primary.main, 0.24)}`,
    secondary: `0 8px 16px 0 ${alpha(palette.secondary.main, 0.24)}`,
    info: `0 8px 16px 0 ${alpha(palette.info.main, 0.24)}`,
    success: `0 8px 16px 0 ${alpha(palette.success.main, 0.24)}`,
    warning: `0 8px 16px 0 ${alpha(palette.warning.main, 0.24)}`,
    error: `0 8px 16px 0 ${alpha(palette.error.main, 0.24)}`,
  };
};

export const createAppTheme = (
  walletThemeInput: ResolvedWalletTheme = DEFAULT_WALLET_THEME
) => {
  const walletTheme = normalizeWalletTheme(walletThemeInput);
  const grey = walletTheme.grey;
  const grey500 = grey["500"];

  const palette = {
    common: { black: "#000", white: "#fff" },
    primary: extendPaletteColor(walletTheme.primary),
    secondary: extendPaletteColor(walletTheme.secondary),
    info: extendPaletteColor(walletTheme.info),
    success: extendPaletteColor(walletTheme.success),
    warning: extendPaletteColor(walletTheme.warning),
    error: extendPaletteColor(walletTheme.error),
    grey: {
      ...grey,
      0: "#FFFFFF",
      500_8: alpha(grey500, 0.08),
      500_12: alpha(grey500, 0.12),
      500_16: alpha(grey500, 0.16),
      500_24: alpha(grey500, 0.24),
      500_32: alpha(grey500, 0.32),
      500_48: alpha(grey500, 0.48),
      500_56: alpha(grey500, 0.56),
      500_80: alpha(grey500, 0.8),
    },
    gradients: {
      primary: createGradient(
        walletTheme.primary.light,
        walletTheme.primary.main
      ),
      info: createGradient(walletTheme.info.light, walletTheme.info.main),
      success: createGradient(
        walletTheme.success.light,
        walletTheme.success.main
      ),
      warning: createGradient(
        walletTheme.warning.light,
        walletTheme.warning.main
      ),
      error: createGradient(walletTheme.error.light, walletTheme.error.main),
    },
    divider: alpha(grey500, 0.24),
    text: walletTheme.text,
    background: walletTheme.background,
    action: {
      active: grey["600"],
      hover: alpha(grey500, 0.08),
      selected: alpha(grey500, 0.16),
      disabled: alpha(grey500, 0.8),
      disabledBackground: alpha(grey500, 0.24),
      focus: alpha(grey500, 0.24),
      hoverOpacity: 0.08,
      disabledOpacity: 0.48,
    },
    white: {
      main: "#fff",
    },
    outerSpace: {
      main: grey["700"],
    },
    brand: {
      auth: walletTheme.authPageColor,
      countdownCalm: walletTheme.countdownTimer.calm,
      countdownWarning: walletTheme.countdownTimer.warning,
      countdownCritical: walletTheme.countdownTimer.critical,
    },
  };

  return createTheme({
    shape: { borderRadius: 8 },
    shadows: createShadow(grey500),
    customShadows: createCustomShadow(grey500, walletTheme),
    typography: {
      ...typography,
    },
    palette,
    components: {
      MuiCard: {
        defaultProps: {
          elevation: 20,
        },
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            pageTitle: "h1",
            pageMeta: "p",
            sectionTitle: "h2",
            sectionLabel: "p",
            heroAmount: "p",
            heroAmountSm: "p",
            ticker: "span",
            tickerSm: "span",
            networkBadge: "span",
            chipLabel: "span",
            statusLabel: "span",
            statusLabelMd: "span",
            cellPrimary: "p",
            cellSecondary: "p",
            amount: "p",
            amountStrong: "p",
            cellMono: "p",
            cellMonoSm: "p",
            actionLink: "span",
          },
        },
      },
    },
  });
};

export default createAppTheme(DEFAULT_WALLET_THEME);
